/**
 * Created by Ella Ma on 2020/09/11.
 * ------ maintenance history ------
 * Modified by Simon Zhao on 2/10/2022 added support for conditional gateway.
 */

import { Component, Input, OnInit } from '@angular/core';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { currentWorkflowAllFieldsSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { DiagramConditionalDialogOptions } from '../diagram-conditional-gateway-dialog/diagram-conditional-gateway-dialog.model';
import { DiagramConditionalGatewayDialogService } from '../diagram-conditional-gateway-dialog/diagram-conditional-gateway-dialog.service';
import { BranchType, DiagramBranch, DiagramNode } from '../diagram-service';
import { DiagramService } from '../diagram.service';
import { EntityService } from './../../../tamalelibs/services/entity.service';
import { EntityBrief } from './../../../tamalelibs/models/entity-brief.model';
import { map, catchError } from 'rxjs/operators';
import { Subscription, of } from 'rxjs';
import { OnDestroy } from '@angular/core';
import { ControlType } from './../../../tamalelibs/models/template-control.model';
import { GatewayType } from '../../../tamalelibs/models/workflow.model';
import { DiagramConditionModel } from '../../../models/workflow-condition.model';
import { TypeUtilities } from '../../../services/utilities/type-utilities';
@Component({
    selector: 'tam-diagram-branch-node',
    templateUrl: './diagram-branch-node.component.html',
    styleUrls: ['./diagram-node.component.scss']
})
export class DiagramBranchNodeComponent implements OnInit, OnDestroy {
    /**
     * a flag indicating whether current node is a conditional gateway node.
     */
    get isConditionalNode() {
        return !!this.node && this.node.type === GatewayType.CONDITIONAL;
    }

    @Input() set nodeId(guid) {
        if (!this.node || this.node.guid !== guid) {
            this.node = this._nodesService.getNode(guid);
            this.node.branchs.forEach(branch => this.onConditionChanged(branch));
        }
    }


    node: DiagramNode;

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _nodesService: DiagramService,
        private _conditionalDialogService: DiagramConditionalGatewayDialogService,
        private _storeQueriery: StoreQuerierService,
        private _entityService: EntityService,
    ) { }

    getConditionTooltip(condition: { field: string, operator: string, expectedValue: string }): string {
        const blank_space_character = ` `;
        return `${condition.field}${blank_space_character}${condition.operator}${blank_space_character}${condition.expectedValue}`;
    }

    getConditionArrayTooltip(conditions: Array<{ field: string, operator: string, expectedValue: string }>, startIndex: number = 0): string {
        const line_break_character = '\n';
        let result = '';
        for (let i = startIndex; i < conditions.length; i++) {
            if (result.length > 0) {
                result += line_break_character;
            }

            result += this.getConditionTooltip(conditions[i]);
        }

        return result;
    }

    isBranchConditionCardVisible(branch: DiagramBranch) {
        return !!branch && !!branch.type && branch.type === BranchType.ConditionalBranch;
    }

    ngOnInit() {
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onConditionChanged(branch: DiagramBranch) {
        if (DiagramBranch.isBlankCondition(branch.condition)) {
            branch.conditionDisplayLines = null;
        } else {
            const conditionModels = DiagramConditionModel.import(branch.condition);
            branch.conditionDisplayLines = [];
            const allControls = this._storeQueriery.queryBySelector(currentWorkflowAllFieldsSelector);
            // function for converting the field id to the field display name.
            const getFldNameFunc = (fldId: string) => {
                const field: any = Array.from(allControls).find((fld: any) => fld.fieldDefinitionId === fldId);
                if (field) {
                    return field.label;
                } else {
                    // probably, found a deleted field.
                    return fldId;
                }
            };
            const entityIdMap = {};
            conditionModels.forEach(con => {
                let valueStr = '';
                if (Array.isArray(con.expectedValue)) {
                    // there's an assumption, only the entity condition is able to have type of array expected values.
                    Array.from(con.expectedValue).forEach(entityObj => {
                        if (!entityIdMap[entityObj.id]) {
                            // collect entity ids.
                            entityIdMap[entityObj.id] = true;
                        }

                        if (valueStr.length > 0) {
                            valueStr += ', ';
                        }
                        // avoid to block the condition display, firstly render entity ids.
                        valueStr += entityObj.id;
                    });
                } else {
                    valueStr = con.expectedValue;
                }
                branch.conditionDisplayLines.push({ field: getFldNameFunc(con.fieldId.fieldId), operator: con.operator.value, expectedValue: valueStr });
            });
            const entityIdArray = Object.keys(entityIdMap);
            if (entityIdArray.length > 0) {
                // found entity id in conditions, query short name for each of them and replace IDs with corresponding short name.
                this._destroySubscriptions.push(this._entityService.getEntityListByIdsQuick(entityIdArray).pipe(
                    map(response => this._entityService.mapEntityBriefList(response)),
                    catchError((e: any) => {
                        return of(null);
                    })).subscribe(
                        (entityList: Array<EntityBrief>) => {
                            if (branch && branch.conditionDisplayLines && branch.conditionDisplayLines.length > 0 && entityList && entityList.length > 0) {
                                branch.conditionDisplayLines.forEach(conStr => {
                                    if (TypeUtilities.isString(conStr.expectedValue)) {
                                        // only string value need to do the replacement
                                        entityList.forEach(entityItem => conStr.expectedValue = conStr.expectedValue.replace(entityItem.id, entityItem.shortName));
                                    }
                                });
                            }
                        }));
            }
        }
    }

    onClickEvent(event, branch) {
        event.stopPropagation();
        this.node.branchs.forEach(item => {
            item.active = item.guid === branch.guid;
        });
        this.node.clickNodeEventEmit(this._nodesService);
    }

    onDeleteBranch(event, branch) {
        this.node.destory(event, branch, this._nodesService);
    }

    onConditionCardClicked(event, branch) {
        let allControls = this._storeQueriery.queryBySelector(currentWorkflowAllFieldsSelector);
        const dialogOptions = new DiagramConditionalDialogOptions();
        const availableCtrlTypes = [ControlType.TEXT, ControlType.SINGLE_TEXT_DROP_DOWN, ControlType.MULTI_TEXT_DROP_DOWN, ControlType.NUMBER, ControlType.SINGLE_ENTITY_DROP_DOWN, ControlType.MULTI_ENTITY_DROP_DOWN, ControlType.SINGLE_USER_DROP_DOWN];
        allControls = allControls.filter(ctrl => availableCtrlTypes.includes(ctrl.type));
        dialogOptions.fields = allControls;
        dialogOptions.condition = branch.condition;
        this._conditionalDialogService.dialogOpen$.next(dialogOptions);
        const closeSubscription = this._conditionalDialogService.dialogClose$.subscribe(
            dialogResult => {
                if (dialogResult.isConfirmed) {
                    branch.condition = dialogResult.conditionResult;
                    this.onConditionChanged(branch);
                }

                if (closeSubscription) {
                    closeSubscription.unsubscribe();
                }
            }
        );
    }
}
